import type { LegalDocId } from './consent';

export interface LegalSection {
  heading: string;
  body: string;
}

export interface LegalDocument {
  title: string;
  effectiveDate: string;
  sections: readonly LegalSection[];
}

/**
 * Тексты правовых документов приложения «Интерстеллар».
 * Соответствуют 152-ФЗ «О персональных данных», 242-ФЗ «О локализации»,
 * 436-ФЗ «О защите детей», ЗоЗПП, требованиям Telegram Bot Developer ToS.
 *
 * ⚠️ ВАЖНО — ПЛЕЙСХОЛДЕРЫ:
 *   Капустин Роман Денисович      — ФИО самозанятого / ИП / название ЮЛ
 *   421212787931      — ИНН (12 цифр для физлица, 10 для ЮЛ)
 *   Самозанятый (плательщик НПД)   — «Самозанятый (плательщик НПД)» / «ИП» / «ООО»
 *   —     — ОГРН/ОГРНИП (если ИП/ЮЛ)
 *   apppartners@mail.ru            — реальный email поддержки
 *   apppartners@mail.ru            — реальный email DPO / приватности
 *   InterstellarAiBot             — username бота без @ (для ссылок)
 *
 * Перед выкатыванием в прод: глобальный поиск-замена этих маркеров.
 * Проверить публикацию финальных текстов через @BotFather → Bot Settings →
 * Configure Mini App → Privacy Policy URL.
 *
 * ⚠️ ПЕРЕД ПРОДОМ: получить юридическое заключение профильного юриста
 * (рекомендую: lawvine.ru, digital-rights.center). Тексты — техническая
 * основа, не замена консультации.
 */

const EFFECTIVE_DATE = '17 мая 2026';

export const LEGAL_CONTENT: Record<LegalDocId, LegalDocument> = {
  // ════════════════════════════════════════════════════════════════════
  // 1. POLITIKA KONFIDENTSIAL'NOSTI
  // ════════════════════════════════════════════════════════════════════
  privacy_policy: {
    title: 'Политика конфиденциальности',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: '1. Общие положения',
        body:
          'Настоящая Политика регулирует обработку персональных данных пользователей Telegram Mini App ' +
          '«Интерстеллар» (далее — Сервис) в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ ' +
          '«О персональных данных» (далее — 152-ФЗ).\n\n' +
          'Оператор персональных данных: Капустин Роман Денисович, Самозанятый (плательщик НПД), ИНН ' +
          '421212787931. Контакт по вопросам обработки ПДн: apppartners@mail.ru.\n\n' +
          'Используя Сервис, вы подтверждаете, что ознакомились с настоящей Политикой и согласны с ' +
          'её условиями. Согласие на обработку персональных данных оформляется отдельным документом.',
      },
      {
        heading: '2. Какие данные мы обрабатываем',
        body:
          'Из Telegram (через initData) получаем:\n' +
          '• Telegram user_id (числовой идентификатор).\n' +
          '• Имя и фамилия (как указано в вашем профиле Telegram).\n' +
          '• Username (если установлен).\n' +
          '• Язык интерфейса Telegram.\n' +
          '• URL аватарки профиля (если установлен).\n\n' +
          'Создаваемые вами данные:\n' +
          '• Тексты сообщений в чатах с персонажами.\n' +
          '• История ваших переписок (метаданные: время, идентификатор персонажа).\n' +
          '• Кастомные персонажи, которые вы создаёте (хранятся локально на вашем устройстве).\n' +
          '• Информация о платежах при оформлении подписки через ЮKassa: идентификатор платежа, ' +
          'сумма, дата, статус. Реквизиты банковской карты мы НЕ получаем и НЕ храним — они ' +
          'обрабатываются на стороне ЮKassa.\n\n' +
          'Мы НЕ запрашиваем и НЕ собираем: реальное имя, email, номер телефона, геолокацию, ' +
          'контакты, фото из галереи и любые другие данные кроме перечисленных выше.',
      },
      {
        heading: '3. Цели обработки',
        body:
          '• Идентификация и аутентификация пользователя через Telegram initData.\n' +
          '• Предоставление функционала чата с виртуальными персонажами на основе ИИ-моделей.\n' +
          '• Сохранение истории ваших разговоров для возможности продолжения диалогов.\n' +
          '• Обработка платежей и предоставление платных функций (подписки, Day Pass).\n' +
          '• Защита от злоупотреблений, ботов и нарушений правил Сервиса.\n' +
          '• Анализ агрегированной статистики использования (без идентификации конкретного пользователя).',
      },
      {
        heading: '4. Использование технологических партнёров',
        body:
          'Для генерации ответов виртуальных персонажей мы используем технологии искусственного ' +
          'интеллекта. Тексты ваших сообщений временно обрабатываются техническими партнёрами — ' +
          'поставщиками вычислительной инфраструктуры и AI-моделей — исключительно для целей ' +
          'формирования ответа в реальном времени.\n\n' +
          'Технологические партнёры не используют ваши сообщения для обучения моделей и не хранят их ' +
          'дольше, чем необходимо для обработки запроса. Передача осуществляется по защищённому ' +
          'каналу (HTTPS/TLS).\n\n' +
          'Помимо технологических партнёров, мы НЕ передаём ваши данные третьим лицам, за исключением ' +
          'случаев, прямо предусмотренных законодательством РФ (запросы правоохранительных органов в ' +
          'установленной форме).',
      },
      {
        heading: '5. Хранение данных',
        body:
          'Первичная запись и хранение персональных данных пользователей — граждан РФ осуществляется ' +
          'на серверах, расположенных на территории Российской Федерации, в соответствии с требованиями ' +
          '242-ФЗ «О локализации».\n\n' +
          'Кастомные персонажи и часть истории переписок хранятся локально на вашем устройстве (в ' +
          'localStorage Telegram Mini App) и не передаются на наши серверы.\n\n' +
          'Срок хранения данных на наших серверах: до момента отзыва согласия, удаления учётной записи ' +
          'или 3 года неактивности (что наступит раньше). После удаления данные не подлежат восстановлению.',
      },
      {
        heading: '6. Ваши права (ст. 14 152-ФЗ)',
        body:
          'Вы имеете право:\n' +
          '• Получить информацию об обработке ваших персональных данных.\n' +
          '• Требовать исправления неточных или неполных данных.\n' +
          '• Требовать удаления данных (см. раздел 7).\n' +
          '• Отозвать согласие на обработку в любой момент.\n' +
          '• Обратиться к Оператору с требованиями и жалобами по адресу apppartners@mail.ru.\n' +
          '• Подать жалобу в уполномоченный орган — Роскомнадзор (rkn.gov.ru).',
      },
      {
        heading: '7. Удаление данных',
        body:
          'Для удаления ваших данных:\n' +
          '• Локальные данные (история переписок, кастомные персонажи): Профиль → Удалить мои данные.\n' +
          '• Серверные данные (учётная запись, история платежей): отправьте запрос на apppartners@mail.ru с ' +
          'указанием вашего Telegram user_id. Удаление производится в течение 30 дней с даты запроса.\n\n' +
          'Активные подписки до момента истечения оплаченного периода сохраняются для исполнения ' +
          'обязательств по ЗоЗПП.',
      },
      {
        heading: '8. Меры безопасности',
        body:
          'Мы применяем технические и организационные меры для защиты данных от несанкционированного ' +
          'доступа, изменения, раскрытия или уничтожения:\n' +
          '• Шифрование канала связи (HTTPS/TLS 1.3) между всеми компонентами Сервиса.\n' +
          '• Шифрование чувствительных данных (реквизиты партнёров) алгоритмом AES-256-GCM.\n' +
          '• Аутентификация пользователей через HMAC-подпись initData от Telegram.\n' +
          '• Ограничение доступа персонала к данным по принципу минимальных привилегий.',
      },
      {
        heading: '9. Изменения политики',
        body:
          'Оператор вправе вносить изменения в настоящую Политику. Существенные изменения вступают ' +
          'в силу после уведомления пользователя в Сервисе и получения нового согласия. Дата последнего ' +
          'обновления указана в начале документа.',
      },
      {
        heading: '10. Контакты',
        body:
          'Оператор персональных данных: Капустин Роман Денисович\n' +
          'Статус: Самозанятый (плательщик НПД)\n' +
          'ИНН: 421212787931\n' +
          'Email для запросов по ПДн: apppartners@mail.ru\n' +
          'Email поддержки: apppartners@mail.ru',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 2. POL'ZOVATEL'SKOYE SOGLASHENIYE (Terms of Service)
  // ════════════════════════════════════════════════════════════════════
  terms_of_service: {
    title: 'Пользовательское соглашение',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: '1. Предмет соглашения',
        body:
          'Настоящее Соглашение регулирует условия использования Telegram Mini App «Интерстеллар» ' +
          '(далее — Сервис) — развлекательного веб-сервиса, доступного через мессенджер Telegram, ' +
          'предоставляющего возможность диалогов с виртуальными персонажами на основе технологий ' +
          'искусственного интеллекта.\n\n' +
          'Стороны: Правообладатель Сервиса — Капустин Роман Денисович (Самозанятый (плательщик НПД), ИНН ' +
          '421212787931); Пользователь — физическое лицо, использующее Сервис через свой ' +
          'аккаунт Telegram.\n\n' +
          'Использование Сервиса означает безусловное принятие настоящего Соглашения. Если вы не ' +
          'согласны с любым из пунктов — прекратите использование Сервиса.',
      },
      {
        heading: '2. Характер сервиса и ограничения',
        body:
          'Все ответы виртуальных персонажей генерируются нейросетевыми моделями искусственного ' +
          'интеллекта (далее — ИИ-моделями) и носят исключительно развлекательный характер.\n\n' +
          'Виртуальные персонажи являются художественными интерпретациями исторических личностей ' +
          'и литературных образов на основе общедоступной информации. Их ответы НЕ отражают ' +
          'реальных взглядов, мнений или высказываний исторических лиц.\n\n' +
          'Сервис НЕ является источником медицинских, юридических, финансовых, психологических или ' +
          'иных профессиональных консультаций. Ответы персонажей не следует воспринимать как ' +
          'руководство к действию или профессиональный совет.',
      },
      {
        heading: '3. Возрастные ограничения (436-ФЗ)',
        body:
          'Минимальный возраст использования базового функционала — 12 лет. Лица младше 12 лет могут ' +
          'использовать Сервис только под контролем родителей или законных представителей.\n\n' +
          'Контент с маркером 18+ (включая флирт, романтические темы, темы откровенного характера) ' +
          'доступен ИСКЛЮЧИТЕЛЬНО совершеннолетним пользователям (от 18 лет), подтвердившим свой ' +
          'возраст при первом доступе к такому контенту.\n\n' +
          'Доступ к 18+ контенту предоставляется только пользователям с активной подпиской Premium ' +
          'и требует отдельного подтверждения возраста через возрастной экран.',
      },
      {
        heading: '4. Запрещённые действия',
        body:
          'Пользователю запрещено:\n' +
          '• Использовать Сервис для распространения экстремистских, террористических, ксенофобных ' +
          'материалов или призывов к насилию (УК РФ ст. 280, 282; КоАП РФ ст. 20.3).\n' +
          '• Создавать или запрашивать материалы, нарушающие права несовершеннолетних.\n' +
          '• Пытаться обойти технические ограничения Сервиса (jailbreak prompts, попытки извлечь ' +
          'системные инструкции, реверс-инжиниринг).\n' +
          '• Использовать Сервис для незаконной деятельности.\n' +
          '• Создавать персонажей реально существующих людей без их согласия или для целей ' +
          'клеветы, угроз, домогательств.\n' +
          '• Намеренно вводить в чаты свои реальные персональные данные, банковские реквизиты, ' +
          'медицинские данные или иную чувствительную информацию.\n' +
          '• Создавать множественные аккаунты для обхода лимитов бесплатного тарифа.\n\n' +
          'Нарушение влечёт ограничение или прекращение доступа к Сервису без возврата средств за ' +
          'использованный период.',
      },
      {
        heading: '5. Тарифы и платные функции',
        body:
          'Сервис предоставляет следующие тарифные планы:\n\n' +
          '• Free (бесплатно) — 10 сообщений в день, базовый набор персонажей без контента 18+. ' +
          'Лимит обновляется в 00:00 UTC ежедневно.\n' +
          '• Basic — 300 ₽ в месяц, 50 сообщений в день.\n' +
          '• Premium — 750 ₽ в месяц, 200 сообщений в день, доступ к 18+ персонажам, ' +
          'расширенная память диалогов.\n' +
          '• Day Pass — 75 ₽ единоразово, даёт +100 сообщений сверх дневного лимита на 24 часа. ' +
          'Активируется только при наличии действующей подписки Basic или Premium.\n\n' +
          'Оплата производится через сервис ЮKassa (ООО НКО «ЮMoney», ИНН 7750005725) с использованием ' +
          'банковской карты, СБП или иных способов, поддерживаемых ЮKassa. Реквизиты карты обрабатываются ' +
          'непосредственно ЮKassa и не передаются Исполнителю.',
      },
      {
        heading: '6. Отмена подписки и возврат средств',
        body:
          'Отмена автопродления. Подписки Basic и Premium продлеваются автоматически каждый ' +
          'календарный месяц через сохранённый платёжный метод ЮKassa, пока вы не отмените автопродление. ' +
          'Способы:\n' +
          '• В Сервисе: Профиль → Подписка → Отключить автопродление.\n' +
          '• Через команду /paysupport в боте @InterstellarAiBot.\n' +
          '• Письмом на apppartners@mail.ru с указанием Telegram user_id.\n\n' +
          'Отмена должна быть произведена не позднее, чем за 24 часа до даты следующего автосписания. ' +
          'После отмены вы продолжаете пользоваться платными функциями до конца уже оплаченного ' +
          'периода — деньги за оплаченный месяц не возвращаются автоматически.\n\n' +
          'Досрочный возврат средств (ЗоЗПП ст. 32). Вы вправе отказаться от подписки в любой ' +
          'момент. Сумма возврата за досрочный отказ рассчитывается по следующей методике:\n\n' +
          'Возврат = Стоимость подписки × min(оставшиеся дни оплаченного периода / 30; ' +
          'оставшийся лимит сообщений / месячный лимит) − комиссия платёжной системы (≈ 3%).\n\n' +
          'Из двух способов расчёта (по дням или по сообщениям) применяется тот, который даёт меньшую ' +
          'сумму возврата — это соответствует фактическому объёму потреблённой услуги. Возврат ' +
          'осуществляется на ту же банковскую карту, с которой была произведена оплата. Срок ' +
          'зачисления: до 10 рабочих дней.\n\n' +
          'Лимит на возвраты. Пользователь вправе запросить досрочный возврат не более одного раза ' +
          'в течение 12 месяцев. Это ограничение направлено на предотвращение злоупотреблений и не ' +
          'затрагивает права потребителя на возврат при некачественном оказании услуги.\n\n' +
          'Day Pass. Согласно п. 11 Перечня товаров, не подлежащих обмену (ПП РФ № 55), Day Pass как ' +
          'цифровой контент с немедленным предоставлением возврату не подлежит после активации ' +
          '(отправки первого сообщения сверх дневного лимита). До активации — полный возврат по запросу.\n\n' +
          'Отказ в возврате. Возврат не производится в случаях:\n' +
          '• подтверждённого фрод-поведения (множественные аккаунты, обход лимитов);\n' +
          '• грубого нарушения Пользовательского соглашения (jailbreak prompts, ввод чувствительных ' +
          'персональных данных третьих лиц, использование в незаконных целях);\n' +
          '• превышения лимита возвратов (более одного за 12 месяцев), за исключением случаев ' +
          'некачественного оказания услуги.\n\n' +
          'Запрос на возврат. Подаётся через команду /paysupport боту @InterstellarAiBot либо письмом ' +
          'на apppartners@mail.ru с указанием Telegram user_id и обоснования. Срок рассмотрения — до ' +
          '10 рабочих дней с даты получения запроса.',
      },
      {
        heading: '7. Ограничение ответственности',
        body:
          'Сервис предоставляется «как есть» (AS IS). Правообладатель не несёт ответственности за:\n' +
          '• Решения, принятые пользователем на основании ответов виртуальных персонажей.\n' +
          '• Временную недоступность Сервиса по техническим причинам, включая сбои Telegram, провайдеров ' +
          'связи, инфраструктуры хостинга.\n' +
          '• Перебои в работе AI-моделей или их деградацию качества со стороны технологических ' +
          'партнёров.\n' +
          '• Утрату локальных данных пользователя при очистке кэша Telegram или удалении приложения.\n\n' +
          'Максимальная ответственность Правообладателя ограничена суммой подписки, оплаченной ' +
          'пользователем за последний месяц.',
      },
      {
        heading: '8. Интеллектуальная собственность',
        body:
          'Все исключительные права на Сервис (дизайн, программный код, тексты системных ' +
          'инструкций персонажей, графические элементы) принадлежат Правообладателю. Пользователю ' +
          'предоставляется неисключительная, отзывная, безвозмездная лицензия на использование ' +
          'Сервиса в личных некоммерческих целях.\n\n' +
          'Кастомные персонажи, созданные пользователем, остаются в его собственности и хранятся ' +
          'локально на устройстве пользователя.',
      },
      {
        heading: '9. Изменения соглашения',
        body:
          'Правообладатель вправе вносить изменения в условия Соглашения. Существенные изменения ' +
          'вступают в силу после уведомления пользователя в Сервисе и могут потребовать повторного ' +
          'подтверждения согласия. Использование Сервиса после внесения изменений означает их принятие.',
      },
      {
        heading: '10. Применимое право и разрешение споров',
        body:
          'К настоящему Соглашению применяется законодательство Российской Федерации. Споры ' +
          'разрешаются путём переговоров через apppartners@mail.ru. В случае невозможности достичь ' +
          'согласия — в судебном порядке по месту нахождения Правообладателя в соответствии с ' +
          'процессуальным законодательством РФ.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 3. SOGLASIE NA OPD (отдельный документ, ст. 9 152-ФЗ)
  // ════════════════════════════════════════════════════════════════════
  personal_data: {
    title: 'Согласие на обработку персональных данных',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: 'Преамбула',
        body:
          'В соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных» я, ' +
          'пользователь Telegram Mini App «Интерстеллар», даю своё согласие Оператору — ' +
          'Капустин Роман Денисович (Самозанятый (плательщик НПД), ИНН 421212787931) — на ' +
          'обработку моих персональных данных на условиях, изложенных ниже.',
      },
      {
        heading: '1. Перечень персональных данных',
        body:
          'Согласие распространяется на следующие категории данных:\n' +
          '• Идентификатор пользователя Telegram (telegram_user_id).\n' +
          '• Имя, фамилия, username из публичного профиля Telegram.\n' +
          '• Язык интерфейса, URL аватарки (если установлен).\n' +
          '• Тексты моих сообщений в чатах с виртуальными персонажами.\n' +
          '• Метаданные использования Сервиса: идентификаторы сессий, время отправки сообщений, ' +
          'идентификатор персонажа, тариф и его статус.\n' +
          '• Данные о платежах через ЮKassa: суммы, даты, идентификаторы транзакций. ' +
          'Реквизиты банковских карт не обрабатываются и не хранятся Оператором.',
      },
      {
        heading: '2. Цели обработки',
        body:
          'Согласие даётся в целях:\n' +
          '• Идентификации и аутентификации пользователя.\n' +
          '• Предоставления функционала диалогов с виртуальными персонажами на основе ИИ-моделей.\n' +
          '• Сохранения истории моих разговоров.\n' +
          '• Обработки платежей и предоставления платных функций.\n' +
          '• Обеспечения работоспособности, защиты от злоупотреблений и улучшения качества Сервиса.',
      },
      {
        heading: '3. Перечень действий с данными',
        body:
          'Сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), ' +
          'извлечение, использование, передача (предоставление техническим партнёрам для обработки), ' +
          'обезличивание, блокирование, удаление, уничтожение.',
      },
      {
        heading: '4. Способ обработки',
        body:
          'Автоматизированная обработка с использованием средств вычислительной техники. ' +
          'Неавтоматизированная обработка (просмотр персоналом Оператора) применяется только в ' +
          'исключительных случаях — при разрешении технических проблем по моему запросу или при ' +
          'расследовании нарушений правил Сервиса.',
      },
      {
        heading: '5. Срок действия согласия',
        body:
          'Настоящее согласие действует с момента его предоставления и до его отзыва.\n\n' +
          'Я могу отозвать согласие в любой момент следующими способами:\n' +
          '• Через настройки приложения: Профиль → Удалить мои данные.\n' +
          '• Письменно по адресу: apppartners@mail.ru.\n\n' +
          'После отзыва согласия мои данные подлежат удалению в течение 30 дней, за исключением ' +
          'данных, обработка которых требуется для исполнения обязательств перед регулирующими ' +
          'органами (налоговая отчётность, история платежей — 5 лет согласно НК РФ).',
      },
      {
        heading: '6. Подтверждение информированности',
        body:
          'Я подтверждаю, что:\n' +
          '• Ознакомился(ась) с Политикой конфиденциальности.\n' +
          '• Понимаю, какие именно мои данные обрабатываются и в каких целях.\n' +
          '• Знаю о своих правах субъекта персональных данных (ст. 14 152-ФЗ).\n' +
          '• Согласие даётся мной добровольно и в моих интересах.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 4. SUBSCRIPTION (Tarify + vozvrat)
  // ════════════════════════════════════════════════════════════════════
  subscription: {
    title: 'Условия подписки и возврата',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: 'Тарифные планы',
        body:
          '«Интерстеллар» предлагает следующие тарифы:\n\n' +
          'Free (бесплатно):\n' +
          '• 10 сообщений в день (лимит обновляется в 00:00 UTC).\n' +
          '• Базовый набор персонажей (без 18+).\n' +
          '• Создание собственных персонажей без ограничений.\n\n' +
          'Basic — 300 ₽ / месяц:\n' +
          '• 50 сообщений в день.\n' +
          '• Все персонажи без 18+.\n' +
          '• Память диалога на 15 сообщений.\n\n' +
          'Premium — 750 ₽ / месяц:\n' +
          '• 200 сообщений в день.\n' +
          '• Доступ к персонажам с маркером 18+.\n' +
          '• Память диалога на 30 сообщений.\n' +
          '• Расширенные стили общения (mood).\n\n' +
          'Day Pass — 75 ₽ (разовая покупка):\n' +
          '• Даёт +100 дополнительных сообщений сверх дневного лимита текущего тарифа на 24 часа ' +
          '(Basic 50 + Day Pass = 150 в сутки; Premium 200 + Day Pass = 300 в сутки).\n' +
          '• Активируется только при наличии действующей подписки Basic или Premium.',
      },
      {
        heading: 'Порядок оплаты',
        body:
          'Оплата производится через сервис ЮKassa (ООО НКО «ЮMoney», ИНН 7750005725). Поддерживаются ' +
          'способы оплаты: банковская карта (Мир, Visa, Mastercard), Система быстрых платежей (СБП), ' +
          'иные методы из числа предоставляемых ЮKassa.\n\n' +
          'Реквизиты карты вводятся на защищённой странице ЮKassa и не передаются Исполнителю. ' +
          'Подписки Basic и Premium продлеваются автоматически каждый месяц через сохранённый ' +
          'платёжный метод ЮKassa до момента отмены. Day Pass — одноразовая покупка без автопродления.',
      },
      {
        heading: 'Отмена подписки',
        body:
          'Отменить подписку можно в любой момент:\n\n' +
          '1. В Сервисе: Профиль → Подписка → Отключить автопродление.\n\n' +
          '2. Через бота: команда /paysupport боту InterstellarAiBot — оператор поддержки поможет ' +
          'с отменой и возвратом.\n\n' +
          '3. Через email: apppartners@mail.ru с указанием вашего Telegram user_id и номера платежа.\n\n' +
          'Отмена должна быть произведена не позднее, чем за 24 часа до даты следующего автосписания. ' +
          'После отмены платные функции остаются доступны до конца оплаченного периода.',
      },
      {
        heading: 'Возврат средств (ЗоЗПП ст. 32)',
        body:
          'В соответствии со статьёй 32 Закона РФ «О защите прав потребителей» вы вправе отказаться ' +
          'от подписки в любой момент. Сумма возврата за досрочный отказ рассчитывается по следующей ' +
          'методике:\n\n' +
          'Возврат = Стоимость подписки × min(оставшиеся дни оплаченного периода / 30; оставшийся ' +
          'лимит сообщений / месячный лимит) − комиссия платёжной системы (ориентировочно 3%).\n\n' +
          'Из двух способов расчёта (по дням или по сообщениям) применяется тот, который даёт меньшую ' +
          'сумму возврата — это соответствует фактическому объёму потреблённой услуги.\n\n' +
          'Пример: пользователь приобрёл Basic (300 ₽), через 5 дней использовал 200 сообщений из ' +
          'месячного лимита 1500. Возврат = 300 × min(25/30; 1300/1500) = 300 × 0.833 = 250 ₽; ' +
          'за вычетом комиссии ЮKassa (~9 ₽) — к возврату 241 ₽.\n\n' +
          'Лимит на возвраты. Запрос на досрочный возврат может быть подан не более одного раза в ' +
          'течение 12 месяцев. Ограничение не применяется в случае некачественного оказания услуги ' +
          '(длительная недоступность Сервиса, систематические ошибки).\n\n' +
          'Day Pass является цифровым контентом с немедленным предоставлением. В соответствии с п. 11 ' +
          'Перечня товаров, не подлежащих обмену (ПП РФ № 55), возврат Day Pass после активации ' +
          '(отправки первого сообщения сверх дневного лимита) не предусмотрен. До активации — полный ' +
          'возврат по запросу.\n\n' +
          'Отказ в возврате. Возврат не производится в случаях: подтверждённого фрод-поведения ' +
          '(множественные аккаунты, обход лимитов); грубого нарушения Пользовательского соглашения; ' +
          'превышения лимита возвратов; а также после истечения срока действия оплаченного периода.\n\n' +
          'Запрос на возврат подаётся через команду /paysupport боту @InterstellarAiBot или письмом на ' +
          'apppartners@mail.ru с указанием Telegram user_id и обоснования. Срок рассмотрения — до ' +
          '10 рабочих дней. При положительном решении средства возвращаются на ту же платёжную карту, ' +
          'с которой была произведена оплата.',
      },
      {
        heading: 'Изменение цен',
        body:
          'Об изменении стоимости подписок Правообладатель уведомляет пользователей не позднее, чем ' +
          'за 30 дней до даты вступления изменений в силу через интерфейс Сервиса. Активные на момент ' +
          'изменения подписки продолжают действовать по старой цене до конца оплаченного периода. ' +
          'При несогласии с новой ценой пользователь вправе отменить автопродление подписки.',
      },
      {
        heading: 'Технические сбои',
        body:
          'В случае невозможности предоставления оплаченных функций по нашей вине (длительный сбой ' +
          'Сервиса более 24 часов) — пользователь вправе требовать продление подписки на ' +
          'эквивалентный период или возврата пропорциональной части средств. Сбои на стороне ' +
          'Telegram, провайдеров связи или ОС устройства пользователя — не относятся к нашей зоне ' +
          'ответственности.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 5. PUBLICHNAYA OFERTA (для платежей через ЮKassa)
  // ════════════════════════════════════════════════════════════════════
  public_offer: {
    title: 'Публичная оферта',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: '1. Стороны',
        body:
          'Настоящий документ является публичной офертой (далее — Оферта) в соответствии со ' +
          'статьями 435, 437 Гражданского кодекса РФ.\n\n' +
          'Исполнитель: Капустин Роман Денисович\n' +
          'Статус: Самозанятый (плательщик НПД)\n' +
          'ИНН: 421212787931\n' +
          'ОГРН/ОГРНИП: — (при наличии)\n' +
          'Email: apppartners@mail.ru\n\n' +
          'Заказчик: физическое лицо, использующее Сервис «Интерстеллар» в Telegram через свой ' +
          'аккаунт Telegram.',
      },
      {
        heading: '2. Предмет договора',
        body:
          'Исполнитель предоставляет Заказчику доступ к платным функциям информационно-' +
          'развлекательного сервиса «Интерстеллар» (Telegram Mini App, доступного через мессенджер ' +
          'Telegram), а Заказчик обязуется оплатить услуги в соответствии с выбранным тарифом.',
      },
      {
        heading: '3. Акцепт оферты',
        body:
          'Акцептом настоящей Оферты считается совершение Заказчиком оплаты любого из тарифных ' +
          'планов через платёжный сервис ЮKassa. С момента акцепта между Исполнителем и Заказчиком ' +
          'возникают договорные отношения на условиях настоящей Оферты, Пользовательского соглашения ' +
          'и Политики конфиденциальности.',
      },
      {
        heading: '4. Стоимость и порядок оплаты',
        body:
          'Стоимость доступа определяется выбранным тарифным планом и выражена в российских рублях. ' +
          'Актуальные цены указаны в разделе «Подписка» Сервиса и могут изменяться с уведомлением ' +
          'пользователя за 30 дней до вступления изменений в силу.\n\n' +
          'Оплата производится Заказчиком через сервис ЮKassa (ООО НКО «ЮMoney», ИНН 7750005725) ' +
          'с использованием банковской карты, СБП либо иных способов, поддерживаемых ЮKassa. ' +
          'Исполнитель не получает реквизиты банковских карт Заказчика — их обработка осуществляется ' +
          'непосредственно ЮKassa.\n\n' +
          'Моментом исполнения обязательства по оплате считается момент поступления подтверждения ' +
          '(webhook payment.succeeded) от ЮKassa на серверы Исполнителя.',
      },
      {
        heading: '5. Срок предоставления услуг',
        body:
          'Месячные подписки (Basic, Premium) — 30 (тридцать) календарных дней с момента акцепта.\n' +
          'Day Pass — 24 (двадцать четыре) часа с момента активации.\n\n' +
          'Подписки продлеваются автоматически до момента их отмены Заказчиком.',
      },
      {
        heading: '6. Качество услуг',
        body:
          'Исполнитель прилагает разумные усилия для обеспечения работоспособности Сервиса 24/7. ' +
          'Гарантированный уровень доступности — 99.0% времени в месячном периоде (за исключением ' +
          'плановых работ с предварительным уведомлением).\n\n' +
          'Услуги предоставляются «как есть». Качество ответов ИИ-моделей может варьироваться и не ' +
          'является основанием для возврата средств, за исключением случаев полной неработоспособности.',
      },
      {
        heading: '7. Возврат средств',
        body:
          'Заказчик вправе отказаться от подписки в любой момент в соответствии со ст. 32 Закона РФ ' +
          '«О защите прав потребителей». При досрочном отказе сумма возврата рассчитывается по ' +
          'методике, изложенной в документе «Условия подписки и возврата»: ' +
          'Возврат = Стоимость подписки × min(оставшиеся дни / 30; оставшийся лимит сообщений / ' +
          'месячный лимит) − комиссия платёжной системы.\n\n' +
          'Возврат осуществляется на банковскую карту, с которой была произведена оплата, в срок до ' +
          '10 рабочих дней с даты одобрения запроса.\n\n' +
          'Заказчик вправе подать запрос на досрочный возврат не более одного раза в течение 12 месяцев.\n\n' +
          'Day Pass возврату после активации не подлежит (ПП РФ № 55, п. 11). До активации возврат ' +
          'осуществляется в полном объёме.\n\n' +
          'Полные условия — см. документ «Условия подписки и возврата».',
      },
      {
        heading: '8. Реквизиты Исполнителя',
        body:
          'Капустин Роман Денисович\n' +
          'Статус: Самозанятый (плательщик НПД)\n' +
          'ИНН: 421212787931\n' +
          'ОГРН/ОГРНИП: — (если применимо)\n' +
          'Email для обращений: apppartners@mail.ru\n' +
          'Email по вопросам ПДн: apppartners@mail.ru\n' +
          'Telegram-бот для поддержки: @InterstellarAiBot (команда /paysupport)',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 6. PARTNYORSKAYA OFERTA (договор оказания инф.услуг)
  // ════════════════════════════════════════════════════════════════════
  partner_offer: {
    title: 'Партнёрская программа — договор-оферта',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: '1. Стороны и предмет договора',
        body:
          'Настоящий документ является публичной офертой Капустин Роман Денисович, самозанятого, ' +
          'ИНН 421212787931, применяющего специальный налоговый режим «Налог на профессиональный ' +
          'доход» (НПД) (далее — Заказчик), адресованной любому дееспособному физическому лицу, ' +
          'применяющему специальный налоговый режим НПД (далее — Партнёр), о заключении договора ' +
          'на условиях, изложенных ниже.\n\n' +
          'Предметом договора является оказание Партнёром Заказчику услуг по продвижению сервиса ' +
          '«Интерстеллар» в сети Интернет на условиях вознаграждения, размер которого зависит от ' +
          'выручки, поступившей от привлечённых Партнёром пользователей.\n\n' +
          'Акцептом оферты является регистрация в партнёрской программе с проставлением отметки ' +
          'о согласии с условиями договора и получение индивидуального slug-идентификатора.',
      },
      {
        heading: '2. Требования к Партнёру',
        body:
          'Партнёром может стать физическое лицо, отвечающее одновременно следующим требованиям:\n' +
          '• Возраст 18+.\n' +
          '• Гражданин РФ или лицо, зарегистрированное в качестве плательщика НПД в порядке, ' +
          'предусмотренном законом.\n' +
          '• Действующий статус самозанятого, подтверждённый постановкой на учёт в ФНС России.\n' +
          '• Наличие аудитории в социальных сетях (Telegram-канал, ВКонтакте, YouTube или иные ' +
          'платформы), тематика которой соответствует предметной области Сервиса.\n\n' +
          'Заказчик вправе отказать в участии без объяснения причин.',
      },
      {
        heading: '3. Партнёрская ссылка и атрибуция',
        body:
          'После одобрения заявки Партнёр получает уникальный slug и реферальную ссылку формата: ' +
          'https://t.me/InterstellarAiBot/app?startapp=ref_{slug}\n\n' +
          'Пользователь, впервые открывший Сервис по реферальной ссылке Партнёра, закрепляется за ' +
          'Партнёром бессрочно. Все последующие платежи такого пользователя в Сервисе формируют ' +
          'вознаграждение Партнёра.',
      },
      {
        heading: '4. Размер вознаграждения',
        body:
          'Размер партнёрского вознаграждения устанавливается индивидуально для каждого Партнёра ' +
          'и составляет от 30% до 50% от выручки, поступившей от привлечённых пользователей. ' +
          'Точная ставка отображается в личном кабинете Партнёра в Сервисе и фиксируется в момент ' +
          'акцепта Договора.\n\n' +
          'Вознаграждение начисляется по факту фактического поступления выручки на счёт Заказчика ' +
          'и подтверждения платежа (статус «succeeded» во внутренней системе учёта).\n\n' +
          'В случае возврата средств пользователю (refund) ранее начисленное вознаграждение по ' +
          'соответствующему платежу аннулируется.',
      },
      {
        heading: '5. Маркировка рекламы',
        body:
          'Размещение Партнёром информации о Сервисе в сети Интернет признаётся рекламой по ' +
          'смыслу 38-ФЗ «О рекламе» и подлежит обязательной маркировке.\n\n' +
          'До размещения Заказчик регистрирует креатив через оператора рекламных данных (Яндекс ОРД) ' +
          'и передаёт Партнёру erid-токен. Партнёр обязан включить в начало или конец материала:\n' +
          'Реклама. ИНН 421212787931. Erid: [ВЫДАННЫЙ_ERID]\n\n' +
          'В видеоматериалах маркировка дублируется в описании.\n\n' +
          'В течение 30 календарных дней с момента размещения Партнёр предоставляет Заказчику ' +
          'фактическую статистику (дата публикации, охват, переходы) для подачи отчётности в ЕРИР.',
      },
      {
        heading: '6. Запрещённые методы продвижения',
        body:
          'Партнёру запрещено:\n' +
          '• Использовать спам, нежелательные рассылки, автоматические подписки.\n' +
          '• Накручивать переходы по реферальной ссылке с использованием ботов, прокси, эмуляторов.\n' +
          '• Использовать заведомо ложные утверждения о возможностях Сервиса (обещание медицинской ' +
          'помощи, психотерапии, профессиональной консультации).\n' +
          '• Использовать в рекламных материалах изображения, имена или голоса реальных лиц без ' +
          'их согласия.\n' +
          '• Размещать рекламу Сервиса на платформах, заблокированных или признанных экстремистскими ' +
          'на территории РФ, в том числе в социальных сетях Meta Platforms, Inc. ' +
          'Ответственность за такие размещения, осуществлённые по инициативе Партнёра, несёт Партнёр.\n' +
          '• Регистрироваться по собственной реферальной ссылке либо с использованием близких ' +
          'для получения вознаграждения с самого себя.\n\n' +
          'Нарушение является основанием для аннулирования начисленного вознаграждения и ' +
          'расторжения договора.',
      },
      {
        heading: '7. Порядок выплат',
        body:
          'Партнёр вправе запросить выплату при балансе не менее 2 000 (двух тысяч) рублей.\n\n' +
          'Процедура:\n' +
          '1. Партнёр формирует запрос на выплату через личный кабинет.\n' +
          '2. Заказчик рассматривает запрос в течение 5 рабочих дней.\n' +
          '3. После одобрения Партнёр выставляет чек в приложении «Мой налог»:\n' +
          '   • Тип услуги: «Рекламные/маркетинговые услуги»\n' +
          '   • Заказчик: физическое лицо\n' +
          '   • ФИО заказчика: Капустин Роман Денисович\n' +
          '   • ИНН заказчика: 421212787931\n' +
          '   • Ставка налога: 4% (как при оказании услуг физическому лицу)\n' +
          '4. Партнёр загружает номер выставленного чека в личный кабинет.\n' +
          '5. Заказчик в течение 3 рабочих дней перечисляет сумму на банковские реквизиты Партнёра.',
      },
      {
        heading: '8. Налогообложение',
        body:
          'Партнёр самостоятельно уплачивает налог на профессиональный доход (НПД) с полученного ' +
          'вознаграждения по ставке 4% (поскольку Заказчик является физическим лицом — самозанятым, ' +
          'не имеющим статуса ИП или юридического лица).\n\n' +
          'Заказчик НЕ является налоговым агентом и НЕ удерживает налоги с вознаграждения Партнёра.\n\n' +
          'В случае утраты Партнёром статуса плательщика НПД обязательства по выплате приостанавливаются.',
      },
      {
        heading: '9. Защита персональных данных',
        body:
          'Для оформления партнёрства и выплат Партнёр предоставляет: ФИО, ИНН, контактный email/' +
          'телефон, банковские реквизиты. Данные хранятся в зашифрованном виде (AES-256-GCM) и ' +
          'доступны только уполномоченному персоналу для целей расчёта и выплаты вознаграждения.\n\n' +
          'Подробнее — см. документ «Согласие партнёра на обработку персональных данных».',
      },
      {
        heading: '10. Урегулирование разногласий и расторжение',
        body:
          'При выявлении нарушения стороны в первую очередь стремятся урегулировать ситуацию ' +
          'путём переговоров. Заказчик направляет письменное уведомление с предложением устранить ' +
          'нарушение в срок 7–14 календарных дней.\n\n' +
          'Заказчик вправе расторгнуть договор в одностороннем порядке в случае:\n' +
          '• Систематического (более 2 раз) либо грубого нарушения условий после предупреждения.\n' +
          '• Подтверждённого фрод-трафика.\n' +
          '• Утраты статуса плательщика НПД.\n' +
          '• Претензий третьих лиц или госорганов.\n\n' +
          'Партнёр вправе расторгнуть договор в любой момент через личный кабинет или email. ' +
          'Начисленное вознаграждение выплачивается на общих условиях.',
      },
      {
        heading: '11. Контакты',
        body:
          'Все вопросы по партнёрской программе: apppartners@mail.ru\n\n' +
          'Заказчик: Капустин Роман Денисович, самозанятый, ИНН 421212787931\n\n' +
          'Полная редакция договора с дополнительными приложениями доступна по запросу на ' +
          'apppartners@mail.ru.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 7. PARTNER CONSENT na OPD (отдельное согласие для партнёров)
  // ════════════════════════════════════════════════════════════════════
  partner_consent: {
    title: 'Согласие Партнёра на обработку персональных данных',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: 'Преамбула',
        body:
          'В соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных» я, ' +
          'участник партнёрской программы Сервиса «Интерстеллар», даю своё согласие Оператору — ' +
          'Капустин Роман Денисович (Самозанятый (плательщик НПД), ИНН 421212787931) — на ' +
          'обработку моих персональных данных в связи с моим участием в партнёрской программе.',
      },
      {
        heading: '1. Перечень обрабатываемых данных',
        body:
          '• Фамилия, имя, отчество (полностью).\n' +
          '• ИНН.\n' +
          '• Налоговый статус (самозанятый / ИП).\n' +
          '• ОГРН/ОГРНИП (если применимо).\n' +
          '• Платёжные реквизиты для выплат (номер счёта, БИК, наименование банка ИЛИ номер карты).\n' +
          '• Email и номер телефона (при их предоставлении мной).\n' +
          '• Закрывающие документы (чеки самозанятого, акты выполненных работ).\n' +
          '• История начислений и выплат вознаграждения.',
      },
      {
        heading: '2. Цели обработки',
        body:
          '• Идентификация Партнёра.\n' +
          '• Расчёт и осуществление выплат вознаграждения.\n' +
          '• Налоговая и бухгалтерская отчётность Компании.\n' +
          '• Контроль соблюдения условий партнёрской программы.\n' +
          '• Защита от мошенничества и фрода.',
      },
      {
        heading: '3. Способы обработки и хранение',
        body:
          'Автоматизированная обработка средствами вычислительной техники. Чувствительные данные ' +
          '(ИНН, паспортные данные, реквизиты) хранятся в зашифрованном виде с использованием ' +
          'алгоритма AES-256-GCM. Доступ к расшифрованным данным имеют только уполномоченные ' +
          'сотрудники Компании для целей выплат и налоговой отчётности.\n\n' +
          'Первичное хранение данных — на серверах в Российской Федерации (242-ФЗ).',
      },
      {
        heading: '4. Передача третьим лицам',
        body:
          'Передача персональных данных Партнёра третьим лицам осуществляется только в случаях:\n' +
          '• Налоговая отчётность в ФНС РФ (по обязательным формам).\n' +
          '• Платёжный оператор для осуществления выплат (банк/Telegram).\n' +
          '• Запросы правоохранительных или регулирующих органов в установленной законом форме.\n\n' +
          'В иных случаях передача данных третьим лицам не осуществляется.',
      },
      {
        heading: '5. Срок согласия',
        body:
          'Согласие действует с момента предоставления и до его отзыва. Отзыв согласия означает ' +
          'прекращение участия в партнёрской программе.\n\n' +
          'Данные, обработка которых требуется для исполнения налоговых обязательств (история ' +
          'выплат, закрывающие документы), сохраняются в течение 5 (пяти) лет согласно ст. 23 НК РФ ' +
          'независимо от отзыва согласия.',
      },
      {
        heading: '6. Версионирование согласия',
        body:
          'При изменении настоящего согласия Партнёр получает запрос на подтверждение новой версии. ' +
          'Текущая версия согласия отображается в личном кабинете Партнёра.\n\n' +
          'Отказ подтвердить новую версию согласия влечёт приостановку участия в программе до ' +
          'момента подтверждения или прекращения участия по инициативе Партнёра.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 8. ABOUT
  // ════════════════════════════════════════════════════════════════════
  about: {
    title: 'О приложении',
    effectiveDate: EFFECTIVE_DATE,
    sections: [
      {
        heading: 'Версия и сборка',
        body:
          'Версия приложения: 1.0.0\n' +
          'Платформа: Telegram Mini App\n' +
          'Дата сборки: май 2026',
      },
      {
        heading: 'Правообладатель',
        body:
          'Капустин Роман Денисович\n' +
          'Статус: Самозанятый (плательщик НПД)\n' +
          'ИНН: 421212787931\n' +
          'ОГРН/ОГРНИП: — (при наличии)\n' +
          'Адрес для корреспонденции: указывается по запросу через apppartners@mail.ru.',
      },
      {
        heading: 'Контакты',
        body:
          'Поддержка пользователей: apppartners@mail.ru\n' +
          'По вопросам обработки персональных данных: apppartners@mail.ru\n' +
          'Команда поддержки в боте: /paysupport боту @InterstellarAiBot\n' +
          'Реестр операторов ПДн (Роскомнадзор): https://pd.rkn.gov.ru/operators-registry/',
      },
      {
        heading: 'Технологии и зависимости',
        body:
          'Приложение разработано как Telegram Mini App с использованием следующих технологий и ' +
          'открытых библиотек:\n\n' +
          '• React 18 (MIT License)\n' +
          '• Vite (MIT License)\n' +
          '• TypeScript (Apache 2.0)\n' +
          '• @telegram-apps/sdk-react (MIT License)\n' +
          '• Express, better-sqlite3 (на стороне сервера) — MIT License\n\n' +
          'Серверная часть размещается на инфраструктуре в Российской Федерации в соответствии с ' +
          'требованиями 242-ФЗ «О локализации».\n\n' +
          'Полный список открытых лицензий доступен по запросу на apppartners@mail.ru.',
      },
      {
        heading: 'Юридическая информация',
        body:
          'Все товарные знаки, упомянутые в Приложении, принадлежат их владельцам.\n\n' +
          'Виртуальные персонажи являются стилизациями исторических личностей и литературных ' +
          'образов, основанными на общедоступной информации. Все диалоги генерируются ИИ-моделями и ' +
          'носят развлекательный характер. Высказывания виртуальных персонажей не отражают реальных ' +
          'взглядов исторических лиц.',
      },
      {
        heading: 'Применимое законодательство',
        body:
          'Деятельность приложения регулируется законодательством Российской Федерации, в том числе:\n' +
          '• 152-ФЗ «О персональных данных»\n' +
          '• 242-ФЗ «О локализации»\n' +
          '• 436-ФЗ «О защите детей от информации, причиняющей вред их здоровью и развитию»\n' +
          '• Закон РФ «О защите прав потребителей»\n' +
          '• Гражданский кодекс РФ\n' +
          '• Налоговый кодекс РФ',
      },
    ],
  },
};
